// src/components/ResourcesView.tsx
import React, {useMemo, useState} from 'react';
import {Fuel, Pickaxe, Trees, Droplet, Flame, Gem, Settings, MapPin, ChartColumn, TriangleAlert, Crown, Building2} from 'lucide-react';
import {useSimulationStore} from '../../store/useSimulationStore';
import KpiCard from '../common/KpiCard';
import SearchSortControls, {SortOption} from '../common/SearchSortControls';
import StatusBadge, { BadgePreset } from '../common/StatusBadge';
import StatRow from "../common/StatRow";
import ItemList, { ItemRowProps } from '../common/ItemList';
import ProgressBar from '../common/ProgressBar';

const TYPE_META: Record<string, { icon: React.ReactNode; color: string; bg: string; border: string }> = {
   oil: {icon: <Fuel className="w-6 h-6" />, color: 'text-amber-400', bg: 'bg-amber-900/20', border: 'border-amber-800/50'},
   minerals: {icon: <Pickaxe className="w-6 h-6" />, color: 'text-violet-400', bg: 'bg-violet-900/20', border: 'border-violet-800/50'},
   forest: {icon: <Trees className="w-6 h-6" />, color: 'text-emerald-400', bg: 'bg-emerald-900/20', border: 'border-emerald-800/50'},
   water: {icon: <Droplet className="w-6 h-6" />, color: 'text-sky-400', bg: 'bg-sky-900/20', border: 'border-sky-800/50'},
   gas: {icon: <Flame className="w-6 h-6" />, color: 'text-orange-400', bg: 'bg-orange-900/20', border: 'border-orange-800/50'},
   generic: {icon: <span>◆</span>, color: 'text-gray-400', bg: 'bg-gray-800', border: 'border-gray-700'},
};

type SortKey = 'yield' | 'radius' | 'nodes' | 'name' | 'depletion';

function investorBonusYield(shares: number, maxShares: number = 100): number {
   return 1000 * Math.sqrt(shares / Math.max(maxShares, 1));
}

const ResourcesView: React.FC = () => {
   const resourceNodes = useSimulationStore(s => s.resourceNodes);
   const nodes = useSimulationStore(s => s.nodes);
   const investments = useSimulationStore(s => s.investments);
   const setSelectedResourceNodeId = useSimulationStore(s => s.setSelectedResourceNodeId);
   
   const [search, setSearch] = useState('');
   const [typeFilter, setTypeFilter] = useState<string>('all');
   const [sortKey, setSortKey] = useState<SortKey>('yield');
   const [sortAsc, setSortAsc] = useState(false);
   
   // Enrich resource nodes with in-range node count
   const enriched = useMemo(() =>
         resourceNodes.map(rn => ({
            rn,
            inRangeNodes: nodes.filter(n =>
               Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2) <= rn.radius
            ),
         })),
      [resourceNodes, nodes]
   );
   
   const allTypes = Array.from(new Set(resourceNodes.map(r => r.type)));
   
   const filtered = useMemo(() => {
      let result = typeFilter === 'all' ? enriched : enriched.filter(e => e.rn.type === typeFilter);
      
      // Apply search filter
      if (search.trim()) {
         result = result.filter(e =>
            e.rn.name.toLowerCase().includes(search.toLowerCase())
         );
      }
      
      return [...result].sort((a, b) => {
         let av = 0, bv = 0;
         switch (sortKey) {
            case 'yield':
               av = a.rn.yield_per_tick;
               bv = b.rn.yield_per_tick;
               break;
            case 'radius':
               av = a.rn.radius;
               bv = b.rn.radius;
               break;
            case 'nodes':
               av = a.inRangeNodes.length;
               bv = b.inRangeNodes.length;
               break;
            case 'depletion':
               av = (a.rn.current_amount ?? 0) / Math.max(a.rn.capacity ?? 1, 1);
               bv = (b.rn.current_amount ?? 0) / Math.max(b.rn.capacity ?? 1, 1);
               break;
            case 'name':
               return sortAsc
                  ? a.rn.name.localeCompare(b.rn.name)
                  : b.rn.name.localeCompare(a.rn.name);
         }
         return sortAsc ? av - bv : bv - av;
      });
   }, [enriched, typeFilter, sortKey, sortAsc, search]);
   
   const totals = useMemo(() => {
      const depleted = resourceNodes.filter(r => (r.current_amount ?? r.capacity ?? 1) <= 0).length;
      return {
         totalYield: enriched.reduce((s, e) => {
            const rnInvs = investments.filter(inv => inv.resource_id === e.rn.id);
            const bonusTotal = rnInvs.reduce((b, inv) => b + investorBonusYield(inv.shares, e.rn.max_total_shares ?? 100), 0);
            return s + e.inRangeNodes.length * 100 + bonusTotal;
         }, 0),
         totalDeposits: resourceNodes.length,
         nodesWithAccess: new Set(enriched.flatMap(e => e.inRangeNodes.map(n => n.id))).size,
         uncoveredNodes: nodes.length - new Set(enriched.flatMap(e => e.inRangeNodes.map(n => n.id))).size,
         avgDepletion: resourceNodes.length > 0
            ? resourceNodes.reduce((s, r) => s + ((r.current_amount ?? r.capacity ?? 0) / Math.max(r.capacity ?? 1, 1)), 0) / resourceNodes.length * 100
            : 100,
         depletedCount: depleted,
      };
   }, [enriched, resourceNodes, nodes]);
   
   const toggleSort = (k: SortKey) => {
      if (sortKey === k) setSortAsc(p => !p);
      else {
         setSortKey(k);
         setSortAsc(false);
      }
   };
   
   const sortOptions: SortOption<SortKey>[] = [
      {key: 'yield', label: 'Yield'},
      {key: 'nodes', label: 'Nodes'},
      {key: 'radius', label: 'Radius'},
      {key: 'name', label: 'Name'},
      {key: 'depletion', label: 'Reserves'},
   ];
   
   return (
      <div className="space-y-3">
         
         {/* ── KPI cards ── */}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={<Gem className="w-4 h-4" />} label="Deposits"
                     value={totals.totalDeposits} color="text-white" />
            <KpiCard icon={<Settings className="w-4 h-4" />} label="Total Yield / tick"
                     value={`+${totals.totalYield.toLocaleString()}`} color="text-emerald-400" />
            <KpiCard icon={<MapPin className="w-4 h-4" />} label="Nodes With Access"
                     value={totals.nodesWithAccess} color="text-blue-400"
                     sub={`of ${nodes.length} total`} />
            <KpiCard icon={<ChartColumn className="w-4 h-4" />} label="Avg Reserves"
                     value={`${totals.avgDepletion.toFixed(0)}%`}
                     color={totals.avgDepletion > 50 ? 'text-emerald-400' : totals.avgDepletion > 20 ? 'text-yellow-400' : 'text-red-400'}
                     sub={`${totals.depletedCount} depleted`} />
         </div>
         
         {/* ── Filters + sort ── */}
         <SearchSortControls
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by resource name…"
            sortKey={sortKey}
            sortAsc={sortAsc}
            onSortChange={toggleSort}
            sortOptions={sortOptions}
            totalCount={resourceNodes.length}
            filteredCount={filtered.length}
         >
            {/* Type filter */}
            <div className="flex gap-1.5 flex-wrap">
               <StatusBadge
                  onClick={() => setTypeFilter('all')}
                  active={typeFilter === 'all'}
                  className={typeFilter === 'all'
                     ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                     : undefined}
               >
                  All types
               </StatusBadge>
               
               {allTypes.map(t => {
                  const m = TYPE_META[t] ?? TYPE_META.generic;
                  return (
                     <StatusBadge
                        key={t}
                        onClick={() => setTypeFilter(t)}
                        active={typeFilter === t}
                        preset={(t as BadgePreset) ?? 'generic'}
                     >
                        {m.icon} {t}
                     </StatusBadge>
                  );
               })}
            </div>
         </SearchSortControls>
         
         {/* ── Resource node cards ── */}
         {filtered.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-xl py-16 text-center text-gray-600 text-sm">
               No resource nodes found.
            </div>
         ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
               {filtered.map(({rn, inRangeNodes}) => {
                  const meta = TYPE_META[rn.type] ?? TYPE_META.generic;
                  const maxShares = rn.max_total_shares ?? 100;
                  const rnInvestments = investments.filter(inv => inv.resource_id === rn.id);
                  const totalSharesSold = rnInvestments.reduce((s, inv) => s + inv.shares, 0);
                  const saturationPct = maxShares > 0 ? (totalSharesSold / maxShares) * 100 : 0;
                  const topInvestor = rnInvestments.length > 0
                     ? rnInvestments.reduce((a, b) => a.shares >= b.shares ? a : b)
                     : null;
                  const topInvestorNode = topInvestor ? nodes.find(n => n.id === topInvestor.node_id) : null;
                  const bonusTotal = rnInvestments.reduce((s, inv) => s + investorBonusYield(inv.shares, maxShares), 0);
                  const totalOut = inRangeNodes.length * 100 + bonusTotal;
                  const pct = rn.capacity > 0 ? (rn.current_amount / rn.capacity) * 100 : 100;
                  const hiddenNodes = inRangeNodes.length - 3;
                  const rangeItems: ItemRowProps[] = inRangeNodes.slice(0, 3).map(n => ({
                     id: n.id,
                     label: n.name,
                     badge: n.income_status,
                     value: `${Math.sqrt((n.x - rn.x) ** 2 + (n.y - rn.y) ** 2).toFixed(0)} units`,
                  }));
                  return (
                     <div key={rn.id}
                          onClick={() => setSelectedResourceNodeId(rn.id)}
                          className={`${meta.bg} ${meta.border} border flex flex-col min-h-[320px] rounded-xl p-4 cursor-pointer text-left hover:brightness-125 transition-all`}
                     >
                        
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                           <div className="flex items-center gap-3">
                              <span className="text-2xl flex items-center">{meta.icon}</span>
                              <div>
                                 <div className="font-semibold text-white text-sm">{rn.name}</div>
                                 <div className={`text-xs capitalize ${meta.color}`}>{rn.type}</div>
                              </div>
                           </div>
                           <div className="text-right">
                              <div className={`text-lg font-bold font-mono ${meta.color}`}>
                                 +{Math.round(totalOut).toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-500">total /tick</div>
                           </div>
                        </div>
                        
                        {/* Depletion bar */}
                        <div className={`rounded-lg px-2.5 py-2 mb-3 border ${
                           pct < 20 ? 'bg-gray-900/40 border-gray-800/40' :
                              'bg-black/20 border border-gray-900/20'
                           }`}>
                           <StatRow
                              icon={<Gem className="w-3.5 h-3.5" />}
                              label="Reserves"
                              value={`${pct.toFixed(0)}%`}
                              color={
                                 pct > 50 ? 'text-emerald-400' :
                                    pct > 20 ? 'text-yellow-400' : 'text-red-400'
                              }
                           />
                           
                           <ProgressBar
                              value={Math.max(1, pct)}
                              color={pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-yellow-500' : 'bg-red-500'}
                              className="my-1.5"
                           />
                           
                           <StatRow
                              label={Math.round(rn.current_amount).toLocaleString()}
                              value={Math.round(rn.capacity).toLocaleString()}
                              labelColor="text-gray-600"
                              color="text-gray-600" />
                           {pct < 20 && (
                              <div className="mt-1.5 text-xs text-yellow-600 flex items-center gap-1">
                                 <TriangleAlert className="w-3 h-3 shrink-0" /> Approaching resource depletion.
                              </div>
                           )}
                        </div>
                        
                        {/* Investment saturation bar */}
                        <div className="rounded-lg px-2.5 py-2 mb-3 bg-black/20 border border-gray-900/20">
                           <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-400">Shares sold</span>
                              <div className="flex items-center gap-2">
                                 {topInvestorNode && (
                                    <span className="text-[10px] text-violet-400 font-medium flex items-center gap-1">
                                       <Crown className="w-3 h-3 shrink-0" /> {topInvestorNode.name} ({topInvestor!.shares} shares)
                                    </span>
                                 )}
                                 <span className="text-xs font-mono text-violet-300">{totalSharesSold}/{maxShares}</span>
                              </div>
                           </div>
                           <ProgressBar
                              value={Math.max(0, saturationPct)}
                              color="bg-violet-500"
                              height="h-1.5"
                           />
                        </div>

                        {/* Stats row */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                           {[
                              {
                                 label: 'In range',
                                 value: `${inRangeNodes.length}`,
                                 color: inRangeNodes.length > 0 ? 'text-white' : 'text-gray-600'
                              },
                              {label: 'Regen/tick', value: `+${rn.regen_rate}`, color: 'text-sky-400'},
                              {
                                 label: 'Investors',
                                 value: `${rnInvestments.length}`,
                                 color: rnInvestments.length > 0 ? 'text-violet-400' : 'text-gray-600'
                              },
                           ].map(({label, value, color}) => (
                              <div key={label} className="bg-black/20 rounded-lg p-2 text-center">
                                 <div className={`text-sm font-bold font-mono ${color}`}>{value}</div>
                                 <div className="text-[10px] text-gray-500">{label}</div>
                              </div>
                           ))}
                        </div>
                        
                        {/* ── In-Range Nodes ── */}
                        <div className="mb-3">
                           <div className="flex items-center justify-between mb-1.5">
                              <StatRow icon={<Building2 className="w-3.5 h-3.5" />} label="Nodes in range" value={`${inRangeNodes.length} total`} labelColor="text-gray-400" color="text-gray-600" />
                           </div>
                           {inRangeNodes.length === 0 ? (
                              <p className="text-gray-600 text-xs px-1">No nodes in range.</p>
                           ) : (
                              <div className="space-y-1">
                                 <ItemList
                                    items={rangeItems}
                                    maxVisible={3}
                                    onOverflowClick={() => setSelectedResourceNodeId(rn.id)}
                                    emptyMessage="No nodes in range."
                                 />
                                 {hiddenNodes > 0 && (
                                    <button
                                       onClick={e => {
                                          e.stopPropagation();
                                          setSelectedResourceNodeId(rn.id);
                                       }}
                                       className="text-xs text-gray-600 hover:text-gray-300 px-2.5 py-1 w-full text-left transition-colors"
                                    >
                                       +{hiddenNodes} more — click to inspect
                                    </button>
                                 )}
                              </div>
                           )}
                        </div>
                        
                        
                        {/* Footer */}
                        <div className="mt-auto mb-0 pt-3 border-t border-white/5 text-[10px] text-gray-600 font-mono">
                           {rn.x}, {rn.y}, {rn.z}
                        </div>
                     </div>
                  );
               })}
            </div>
         )}
      </div>
   );
};

export default ResourcesView;
